import JsonView from '@uiw/react-json-view';
import { Brain } from 'lucide-react';

import CollapsibleBlock from '../CollapsibleBlock';

interface ThinkingContentProps {
  content: string;
}

type ContentSegment = {
  type: 'text' | 'json';
  content: string;
  parsed?: unknown;
};

const parseContent = (text: string): ContentSegment[] => {
  const segments: ContentSegment[] = [];
  let currentIndex = 0;
  const jsonPattern =
    /(\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}|\[[^[\]]*(?:\[[^[\]]*\][^[\]]*)*\])/g;

  let match;
  while ((match = jsonPattern.exec(text)) !== null) {
    if (match.index > currentIndex) {
      const textContent = text.slice(currentIndex, match.index);
      if (textContent.trim()) {
        segments.push({ type: 'text', content: textContent });
      }
    }

    try {
      const parsed = JSON.parse(match[0]);
      segments.push({ type: 'json', content: match[0], parsed });
    } catch {
      segments.push({ type: 'text', content: match[0] });
    }

    currentIndex = match.index + match[0].length;
  }

  if (currentIndex < text.length) {
    const remaining = text.slice(currentIndex);
    if (remaining.trim()) {
      segments.push({ type: 'text', content: remaining });
    }
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', content: text });
  }

  return segments;
};

export default function ThinkingContent({ content }: ThinkingContentProps) {
  const segments = parseContent(content);

  return (
    <CollapsibleBlock
      icon={<Brain className="h-3.5 w-3.5" />}
      label="Thoughts"
      tone="amber"
    >
      <div className="space-y-2">
        {segments.map((segment, index) =>
          segment.type === 'json' && segment.parsed ? (
            <JsonView
              key={index}
              value={segment.parsed as object}
              collapsed={false}
              displayDataTypes={false}
              displayObjectSize={false}
              style={
                {
                  '--w-rjv-background-color': 'transparent',
                  '--w-rjv-font-family':
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  '--w-rjv-font-size': '0.875rem',
                  '--w-rjv-key-string': '#22c55e',
                  '--w-rjv-color': '#78350f',
                  '--w-rjv-type-string-color': '#d97706',
                  '--w-rjv-type-int-color': '#2563eb',
                  '--w-rjv-type-boolean-color': '#7c3aed',
                } as React.CSSProperties
              }
            />
          ) : (
            <div
              key={index}
              className="whitespace-pre-wrap font-mono text-sm text-muted-foreground"
            >
              {segment.content}
            </div>
          )
        )}
      </div>
    </CollapsibleBlock>
  );
}
